import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const CATEGORIES = [
  { name: 'Music', icon: 'ri-music-2-line', color: 'bg-rose-50 text-rose-600 border-rose-100', img: 'https://public.readdy.ai/ai/img_res/a91702dddcde74c7f98349f2a216aee3.jpg' },
  { name: 'Tech', icon: 'ri-code-s-slash-line', color: 'bg-teal-50 text-teal-600 border-teal-100', img: 'https://readdy.ai/api/search-image?query=modern%20technology%20conference%20keynote%20presentation%20large%20screen%20audience%20seated%20professional%20event%20clean%20minimal%20stage%20design&width=400&height=260&seq=cat-tech-1&orientation=landscape' },
  { name: 'Art', icon: 'ri-palette-line', color: 'bg-amber-50 text-amber-600 border-amber-100', img: 'https://readdy.ai/api/search-image?query=contemporary%20art%20gallery%20exhibition%20colorful%20paintings%20on%20white%20walls%20visitors%20viewing%20artwork%20elegant%20space%20natural%20lighting&width=400&height=260&seq=cat-art-1&orientation=landscape' },
  { name: 'Sports', icon: 'ri-football-line', color: 'bg-green-50 text-green-600 border-green-100', img: 'https://readdy.ai/api/search-image?query=sports%20stadium%20packed%20crowd%20athletic%20competition%20outdoor%20event%20sunny%20day%20green%20field%20energetic%20atmosphere%20professional%20photography&width=400&height=260&seq=cat-sports-1&orientation=landscape' },
  { name: 'Food', icon: 'ri-restaurant-line', color: 'bg-orange-50 text-orange-600 border-orange-100', img: 'https://readdy.ai/api/search-image?query=food%20festival%20outdoor%20market%20colorful%20food%20stalls%20people%20tasting%20dishes%20vibrant%20atmosphere%20street%20food%20event%20warm%20lighting&width=400&height=260&seq=cat-food-1&orientation=landscape' },
  { name: 'Business', icon: 'ri-briefcase-line', color: 'bg-gray-50 text-gray-600 border-gray-100', img: 'https://readdy.ai/api/search-image?query=professional%20business%20networking%20event%20conference%20room%20people%20shaking%20hands%20modern%20office%20setting%20corporate%20atmosphere%20clean%20background&width=400&height=260&seq=cat-biz-1&orientation=landscape' },
  { name: 'Health', icon: 'ri-heart-pulse-line', color: 'bg-pink-50 text-pink-600 border-pink-100', img: 'https://readdy.ai/api/search-image?query=wellness%20yoga%20retreat%20outdoor%20group%20session%20peaceful%20nature%20setting%20people%20meditating%20healthy%20lifestyle%20morning%20light%20serene%20atmosphere&width=400&height=260&seq=cat-health-1&orientation=landscape' },
  { name: 'Education', icon: 'ri-graduation-cap-line', color: 'bg-cyan-50 text-cyan-600 border-cyan-100', img: 'https://readdy.ai/api/search-image?query=educational%20workshop%20seminar%20students%20learning%20modern%20classroom%20bright%20space%20engaged%20audience%20speaker%20presenting%20knowledge%20sharing%20event&width=400&height=260&seq=cat-edu-1&orientation=landscape' },
  { name: 'Comedy', icon: 'ri-emotion-laugh-line', color: 'bg-yellow-50 text-yellow-600 border-yellow-100', img: 'https://readdy.ai/api/search-image?query=comedy%20show%20stand%20up%20comedian%20on%20stage%20spotlight%20laughing%20audience%20intimate%20venue%20microphone%20entertainment%20night%20event&width=400&height=260&seq=cat-comedy-1&orientation=landscape' },
  { name: 'Fashion', icon: 'ri-t-shirt-line', color: 'bg-purple-50 text-purple-600 border-purple-100', img: 'https://readdy.ai/api/search-image?query=fashion%20show%20runway%20models%20walking%20elegant%20clothing%20audience%20watching%20stylish%20event%20dramatic%20lighting%20luxury%20atmosphere%20photography&width=400&height=260&seq=cat-fashion-1&orientation=landscape' },
  { name: 'Film', icon: 'ri-film-line', color: 'bg-indigo-50 text-indigo-600 border-indigo-100', img: 'https://readdy.ai/api/search-image?query=film%20festival%20outdoor%20cinema%20screening%20audience%20watching%20movie%20under%20stars%20night%20event%20cozy%20atmosphere%20large%20screen%20popcorn&width=400&height=260&seq=cat-film-1&orientation=landscape' },
  { name: 'Networking', icon: 'ri-group-line', color: 'bg-teal-50 text-teal-700 border-teal-100', img: 'https://readdy.ai/api/search-image?query=professional%20networking%20mixer%20event%20people%20mingling%20cocktail%20party%20modern%20venue%20business%20casual%20attire%20warm%20lighting%20social%20gathering&width=400&height=260&seq=cat-net-1&orientation=landscape' },
];

export default function CategoriesPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [favCategories, setFavCategories] = useState<string[]>([]);

  const firstName = profile?.full_name?.split(' ')[0] || user?.user_metadata?.full_name?.split(' ')[0] || null;

  useEffect(() => {
    const PAGE_TITLE = 'Browse Event Categories – Music, Tech, Art & More | EVENTSHOWCASE';
    const PAGE_DESC = 'Find events that match your interests. Browse event categories including Music, Tech, Art, Sports, Food, Business, Health, Education, Comedy, Fashion, Film and more on EVENTSHOWCASE.';
    const PAGE_URL = `${SITE_URL}/categories`;
    const PAGE_IMG = 'https://readdy.ai/api/search-image?query=colorful%20event%20categories%20grid%20music%20tech%20art%20sports%20food%20business%20health%20education%20vibrant%20modern%20clean%20interface%20teal%20green%20minimal%20professional&width=1200&height=630&seq=og-categories&orientation=landscape';

    document.title = PAGE_TITLE;
    fetchCounts();

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [a, v] = sel.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(a.trim(), v.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-page-meta', 'true');
      }
      el.setAttribute(attr, val);
    };

    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[name="keywords"]', 'content', 'event categories, music events, tech events, art events, sports events, food events');
    setMeta('meta[name="robots"]', 'content', 'index, follow');
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[property="og:image"]', 'content', PAGE_IMG);
    setMeta('meta[property="og:image:width"]', 'content', '1200');
    setMeta('meta[property="og:image:height"]', 'content', '630');
    setMeta('meta[property="og:site_name"]', 'content', 'EVENTSHOWCASE');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', PAGE_TITLE);
    setMeta('meta[name="twitter:description"]', 'content', PAGE_DESC);
    setMeta('meta[name="twitter:image"]', 'content', PAGE_IMG);

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.rel = 'canonical';
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = PAGE_URL;

    // Schema.org JSON-LD
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Browse Event Categories',
      description: 'Find events that match your interests. From live music to tech conferences — there\'s something for everyone.',
      url: `${SITE_URL}/categories`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'EVENTSHOWCASE',
        url: SITE_URL,
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Categories', item: `${SITE_URL}/categories` },
        ],
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: CATEGORIES.map((cat, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Thing',
            name: cat.name,
            description: `${cat.name} events on EVENTSHOWCASE`,
            url: `${SITE_URL}/discover?category=${cat.name}`,
          },
        })),
      },
    };

    const existing = document.getElementById('schema-categories');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-categories';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-categories');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  useEffect(() => {
    if (user) fetchFavCategories();
  }, [user]);

  const fetchCounts = async () => {
    const { data } = await supabase
      .from('events')
      .select('category')
      .eq('is_published', true)
      .gte('start_date', new Date().toISOString());
    if (data) {
      const map: Record<string, number> = {};
      data.forEach((e: any) => { map[e.category] = (map[e.category] || 0) + 1; });
      setCounts(map);
    }
  };

  const fetchFavCategories = async () => {
    if (!user) return;
    // Derive favourite categories from past registrations
    const { data } = await supabase
      .from('registrations')
      .select('events(category)')
      .eq('user_id', user.id)
      .limit(20);
    if (data) {
      const freq: Record<string, number> = {};
      data.forEach((r: any) => {
        const cat = r.events?.category;
        if (cat) freq[cat] = (freq[cat] || 0) + 1;
      });
      const sorted = Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([cat]) => cat);
      setFavCategories(sorted.slice(0, 3));
    }
  };

  // Sort: favourites first, then rest
  const sortedCategories = favCategories.length > 0
    ? [
        ...CATEGORIES.filter(c => favCategories.includes(c.name)),
        ...CATEGORIES.filter(c => !favCategories.includes(c.name)),
      ]
    : CATEGORIES;

  return (
    <div className="min-h-screen bg-white">
      <Navbar forceScrolled />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            {firstName ? `What are you into, ${firstName}?` : 'Browse by Category'}
          </h1>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            {firstName
              ? 'We\'ve sorted your favourite categories to the top based on your past events.'
              : 'Find events that match your interests. From live music to tech conferences — there\'s something for everyone.'}
          </p>
          {favCategories.length > 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
              <span className="text-xs text-gray-400">Your top interests:</span>
              {favCategories.map(cat => (
                <button
                  key={cat}
                  onClick={() => navigate(`/discover?category=${cat}`)}
                  className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full hover:bg-teal-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {cat}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        {favCategories.length > 0 && (
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-4">
            <i className="ri-star-line mr-1 text-teal-500"></i> Your favourites first
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {sortedCategories.map(cat => {
            const isFav = favCategories.includes(cat.name);
            return (
              <button
                key={cat.name}
                onClick={() => navigate(`/discover?category=${cat.name}`)}
                className={`group relative rounded-2xl overflow-hidden cursor-pointer text-left transition-all ${
                  isFav ? 'ring-2 ring-teal-400 ring-offset-2' : 'border border-gray-100 hover:border-teal-200'
                }`}
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={cat.img}
                    alt={cat.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                </div>
                {isFav && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-star-fill text-white text-[10px]"></i>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold mb-1 ${cat.color} bg-white/90`}>
                        <i className={cat.icon}></i>
                        {cat.name}
                      </div>
                      <p className="text-white text-xs">
                        {counts[cat.name] ? `${counts[cat.name]} upcoming` : 'Explore events'}
                      </p>
                    </div>
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-teal-500 transition-colors">
                      <i className="ri-arrow-right-line text-white text-sm"></i>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {!user && (
        <section className="bg-gray-50 border-t border-gray-100 py-10 px-4 text-center">
          <p className="text-gray-700 font-medium mb-1">Sign in to get personalised category recommendations</p>
          <p className="text-gray-400 text-sm mb-4">We&apos;ll sort your favourite categories to the top based on your event history</p>
          <Link to="/login" className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap">
            <i className="ri-user-line"></i> Log In
          </Link>
        </section>
      )}

      <Footer />
    </div>
  );
}
